import * as React from "react";
import { useRef, useState } from "react";
import minBy from "lodash/minBy";
import { useFloating } from "@floating-ui/react";
import uniqBy from "lodash/uniqBy";
import OnClickPopoverWrapper from "views/components/visualizations/OnClickPopover/OnClickPopoverWrapper";
import CartesianOnClickPopoverDropdown from "views/components/visualizations/OnClickPopover/CartesianOnClickPopoverDropdown";
import HeatmapOnClickPopover from "views/components/visualizations/OnClickPopover/HeatmapOnClickPopover";
import PieOnClickPopoverDropdown from "views/components/visualizations/OnClickPopover/PieOnClickPopoverDropdown";
import { CANDIDATE_PICK_RADIUS } from "views/components/visualizations/Constants";
import DropdownSwitcher from "views/components/visualizations/OnClickPopover/DropdownSwitcher";
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const getBarElement = (graphDiv, pt, curveNumberRenderIndexMapper) => {
  var _a, _b;
  const { curveNumber, pointIndex } = pt;
  const traceIndex = curveNumberRenderIndexMapper[curveNumber];
  const trace = graphDiv.querySelectorAll(".cartesianlayer .trace")[traceIndex];
  if (!trace) return null;
  const point = trace.querySelectorAll(".point")[pointIndex];
  return (_b = (_a = point == null ? void 0 : point.querySelector("rect")) != null ? _a : point == null ? void 0 : point.querySelector("path")) != null ? _b : null;
};
const listSubplotOrder = (gd) => {
  const subplots = gd.querySelectorAll(".cartesianlayer g.subplot");
  return Array.from(subplots).map((node) => {
    const idClass = Array.from(node.classList).find((cls) => cls !== "subplot");
    return idClass != null ? idClass : "";
  });
};
const createBarElementGetter = (gd) => {
  const fullData = gd._fullData;
  const curveNumberGroupedBySubPlot = {};
  fullData.forEach(({ xaxis, yaxis }, curveNumber) => {
    const subplot = `${xaxis != null ? xaxis : "x"}${yaxis != null ? yaxis : "y"}`;
    (curveNumberGroupedBySubPlot[subplot] || (curveNumberGroupedBySubPlot[subplot] = [])).push(curveNumber);
  });
  const subPlotDOMOrder = listSubplotOrder(gd);
  const curveNumberRenderIndexArray = subPlotDOMOrder.flatMap((subPlotId) => curveNumberGroupedBySubPlot[subPlotId]);
  const curveNumberRenderIndexMapper = Object.fromEntries(curveNumberRenderIndexArray.map((v, i) => [v, i]));
  return (graphDiv, pt) => getBarElement(graphDiv, pt, curveNumberRenderIndexMapper);
};
const getScatterMarkerElement = (graphDiv, pt) => {
  var _a;
  const {
    fullData: { uid },
    pointIndex
  } = pt;
  const trace = graphDiv.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  return (_a = trace.querySelectorAll(".points .point")[pointIndex]) != null ? _a : null;
};
const getPieSliceElement = (_, __, targetEl) => targetEl;
const projectT = (P, A, B) => {
  const vx = B.x - A.x;
  const vy = B.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const len2 = vx * vx + vy * vy || 1;
  return (wx * vx + wy * vy) / len2;
};
const distToRect = (rect, { x, y }) => {
  let dx = 0;
  if (x < rect.left) dx = rect.left - x;
  else if (x > rect.right) dx = x - rect.right;
  let dy = 0;
  if (y < rect.top) dy = rect.top - y;
  else if (y > rect.bottom) dy = y - rect.bottom;
  return Math.hypot(dx, dy);
};
const dataToPagePx = (gd, pt, xVal, yVal) => {
  var _a, _b, _c, _d, _e, _f;
  const fl = gd._fullLayout;
  const xa = ((_a = pt.xaxis) == null ? void 0 : _a.l2p) ? pt.xaxis : fl[(_c = (_b = pt.xaxis) == null ? void 0 : _b._name) != null ? _c : "xaxis"];
  const ya = ((_d = pt.yaxis) == null ? void 0 : _d.l2p) ? pt.yaxis : fl[(_f = (_e = pt.yaxis) == null ? void 0 : _e._name) != null ? _f : "yaxis"];
  if (!(xa == null ? void 0 : xa.l2p) || !(xa == null ? void 0 : xa.d2l) || !(ya == null ? void 0 : ya.l2p) || !(ya == null ? void 0 : ya.d2l)) return null;
  const gx = xa.l2p(xa.d2l(xVal));
  const gy = ya.l2p(ya.d2l(yVal));
  if (!Number.isFinite(gx) || !Number.isFinite(gy)) return null;
  const rect = gd.getBoundingClientRect();
  return { x: rect.left + fl._size.l + gx, y: rect.top + fl._size.t + gy };
};
const pickNearestElementAnchor = (e, candidates) => {
  const { clientX, clientY } = e.event;
  if (!candidates.length) return null;
  const inside = candidates.find(
    ({ rect: rect2 }) => clientX >= rect2.left && clientX <= rect2.right && clientY >= rect2.top && clientY <= rect2.bottom
  );
  const candidatesWithDistances = candidates.map((candidate) => {
    const rect2 = candidate.rect;
    const d = distToRect(rect2, { x: clientX, y: clientY });
    return { d, candidate };
  });
  const pointsInRadius = candidatesWithDistances.filter(({ d }) => d < CANDIDATE_PICK_RADIUS).sort((a, b) => a.d - b.d).map(({ candidate }) => candidate.pt);
  const closest = inside != null ? inside : minBy(candidatesWithDistances, "d").candidate;
  if (!closest) return null;
  const { el, rect, pt } = closest;
  const rel = {
    x: clamp01((clientX - rect.left) / Math.max(rect.width, 1)),
    y: clamp01((clientY - rect.top) / Math.max(rect.height, 1))
  };
  return { el, rel, pt, pointsInRadius };
};
const makeElementAnchor = (e, gd, chartType) => {
  var _a;
  const getEl = chartType === "bar" ? createBarElementGetter(gd) : (graphDiv2, pt, targetEl2) => getPieSliceElement(graphDiv2, pt, targetEl2);
  const graphDiv = gd;
  const targetEl = ((_a = e.event) == null ? void 0 : _a.target) || graphDiv;
  const candidates = e.points.map((pt) => {
    const el = getEl(graphDiv, pt, targetEl);
    return el ? { pt, el, rect: el.getBoundingClientRect() } : null;
  }).filter((candidate) => !!candidate);
  return pickNearestElementAnchor(e, candidates);
};
function pickNearestLinesElementForTrace(gd, pt, click) {
  var _a;
  const uid = (_a = pt.fullData) == null ? void 0 : _a.uid;
  if (!uid) return null;
  const trace = gd.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  const linesGroups = Array.from(trace.querySelectorAll(".lines path.js-line"));
  if (!linesGroups.length) return null;
  const candidates = linesGroups.map((line) => {
    const r = line.getBoundingClientRect();
    const { x, y } = click;
    return { el: line, d: distToRect(r, { x, y }) };
  });
  const best = minBy(candidates, "d");
  return best == null ? void 0 : best.el;
}
const getScatterLineElements = (gd, click, pt) => {
  var _a, _b, _c, _d, _e, _f;
  const fd = (_b = pt.data) != null ? _b : (_a = gd._fullData) == null ? void 0 : _a[pt.curveNumber];
  const xs = (_c = fd == null ? void 0 : fd.x) != null ? _c : [];
  const ys = (_d = fd == null ? void 0 : fd.y) != null ? _d : [];
  const i = (_f = (_e = pt.pointIndex) != null ? _e : pt.pointNumber) != null ? _f : 0;
  const segs = [i > 0 ? [i - 1, i] : null, i < xs.length - 1 ? [i, i + 1] : null].filter((seg) => !!seg);
  return segs.map(([i0, i1]) => {
    const A = dataToPagePx(gd, pt, xs[i0], ys[i0]);
    const B = dataToPagePx(gd, pt, xs[i1], ys[i1]);
    if (!A || !B) return null;
    const t = clamp01(projectT(click, A, B));
    const px = A.x + t * (B.x - A.x);
    const py = A.y + t * (B.y - A.y);
    const d = Math.hypot(click.x - px, click.y - py);
    const el = pickNearestLinesElementForTrace(gd, pt, click);
    const { x: valuePx, y: valuePy } = dataToPagePx(gd, pt, xs[i], ys[i]);
    return { el, pt, d, px, py, valuePx, valuePy };
  });
};
const makeScatterAnchor = (e, gd) => {
  const graphDiv = gd;
  const markerCandidates = e.points.map((pt2) => {
    const el2 = getScatterMarkerElement(graphDiv, pt2);
    return el2 ? { pt: pt2, el: el2, rect: el2.getBoundingClientRect() } : null;
  }).filter((candidate) => !!candidate);
  const bestMarker = pickNearestElementAnchor(e, markerCandidates);
  if (bestMarker) return bestMarker;
  const { clientX, clientY } = e.event;
  const click = { x: clientX, y: clientY };
  const lineCandidates = e.points.flatMap((pt2) => getScatterLineElements(graphDiv, click, pt2)).filter((candidate) => !!candidate);
  const best = minBy(lineCandidates, "d");
  const pointsInRadius = uniqBy(
    lineCandidates.filter(({ d }) => d < CANDIDATE_PICK_RADIUS).sort((a, b) => a.d - b.d).map(({ pt: pt2 }) => pt2),
    "pointIndex"
  );
  if (!best) return null;
  const { el, pt, valuePx, valuePy } = best;
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const rel = {
    x: clamp01((valuePx - rect.left) / Math.max(rect.width, 1)),
    y: clamp01((valuePy - rect.top) / Math.max(rect.height, 1))
  };
  return { rel, el, pt, pointsInRadius };
};
const popoverComponent = (chartType) => {
  switch (chartType) {
    case "heatmap":
      return HeatmapOnClickPopover;
    case "pie":
      return PieOnClickPopoverDropdown;
    default:
      return CartesianOnClickPopoverDropdown;
  }
};
const alignByRelativeCoords = (rel = { x: 0, y: 0 }) => ({
  name: "alignByRelativeCoords",
  options: rel,
  fn: ({ x, y, rects }) => ({
    x: x + rects.reference.width * rel.x,
    y: y + rects.reference.height * rel.y
  })
});
const usePlotOnClickPopover = (chartType, config) => {
  const gdRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const { refs, floatingStyles } = useFloating({
    placement: "top-start",
    elements: {
      reference: anchor == null ? void 0 : anchor.el
    },
    transform: false,
    middleware: [alignByRelativeCoords(anchor == null ? void 0 : anchor.rel)]
  });
  const initializeGraphDivRef = (_, gd) => {
    gdRef.current = gd;
  };
  const onPopoverClose = () => setAnchor(null);
  const onChartClick = (_, e) => {
    var _a, _b, _c;
    const gd = (_c = gdRef.current) != null ? _c : (_b = (_a = e.event) == null ? void 0 : _a.target) == null ? void 0 : _b.closest(".js-plotly-plot");
    if (!gd) return;
    const a = chartType === "scatter" ? makeScatterAnchor(e, gd) : makeElementAnchor(e, gd, chartType);
    if (!a) return;
    setAnchor(a);
  };
  const onPopoverChange = (isOpen) => {
    if (!isOpen) onPopoverClose();
  };
  const isPopoverOpen = !!anchor;
  const PopoverComponent = popoverComponent(chartType);
  const popover = /* @__PURE__ */ React.createElement(
    OnClickPopoverWrapper,
    {
      isPopoverOpen,
      onPopoverChange,
      ref: refs.setFloating,
      style: floatingStyles
    },
    /* @__PURE__ */ React.createElement(
      DropdownSwitcher,
      {
        component: PopoverComponent,
        clickPoint: anchor == null ? void 0 : anchor.pt,
        config,
        clickPointsInRadius: anchor == null ? void 0 : anchor.pointsInRadius,
        onPopoverClose
      }
    )
  );
  return { initializeGraphDivRef, onChartClick, popover };
};
export default usePlotOnClickPopover;
